import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { ArrowLeft, Plus, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const SupportTickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ticketType, setTicketType] = useState('');
  const [description, setDescription] = useState('');
  const [relatedId, setRelatedId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support-tickets`);
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/support-tickets`, {
        type: ticketType,
        related_id: relatedId || null,
        description
      });
      toast.success(t('msg.ticketCreated'));
      setShowCreateModal(false);
      setTicketType('');
      setDescription('');
      setRelatedId('');
      fetchTickets();
    } catch (err) {
      toast.error(t('msg.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      in_review: 'bg-yellow-100 text-yellow-700',
      needs_info: 'bg-orange-100 text-orange-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      completed: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.new;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <button
            data-testid="back-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
              <p className="text-gray-600 mt-1">Get help with refunds, issues, or general queries</p>
            </div>
            <Button
              data-testid="create-ticket-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} className="mr-2" />
              New Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="card p-12 text-center">
            <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No support tickets yet</p>
            <Button data-testid="create-first-ticket-btn" onClick={() => setShowCreateModal(true)}>
              Create Your First Ticket
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="card p-6" data-testid={`ticket-${ticket.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900 capitalize">
                        {ticket.type.replace('_', ' ')}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">{ticket.description}</p>
                    {ticket.related_id && (
                      <p className="text-sm text-gray-500">Related to: {ticket.related_id}</p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Created: {formatDate(ticket.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-ticket-modal">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Ticket Type</label>
              <Select value={ticketType} onValueChange={setTicketType} required>
                <SelectTrigger data-testid="ticket-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="refund">Refund Request</SelectItem>
                  <SelectItem value="dispute">Payment Dispute</SelectItem>
                  <SelectItem value="general">General Support</SelectItem>
                  <SelectItem value="bug">Bug Report</SelectItem>
                  <SelectItem value="content">Content Issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Related Report/Donation ID (Optional)</label>
              <Input
                data-testid="related-id-input"
                type="text"
                placeholder="Enter report or donation ID"
                value={relatedId}
                onChange={(e) => setRelatedId(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                data-testid="ticket-description-input"
                placeholder="Describe your issue in detail"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="submit-ticket-btn"
                type="submit"
                className="flex-1"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Ticket'}
              </Button>
              <Button
                data-testid="cancel-ticket-btn"
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTickets;